/**
 * resetToUTC `applies` the timezone offset in the provided Date
 * updating the time to compensate for the offset and returning a new "local" Date object.
 *
 * This is particularly useful when rendering Dates from other timezones,
 * which have to be displayed as if the user was in the given timezone.
 *
 * For instance, showing 5AM in São Paulo to a user in Germany, we still want to show it as 5AM,
 * rather than 10AM (which is the corresponding local time in Berlin).
 *
 * @param {Date} original original Date (will not be mutated)
 * @returns new Date object with updated time
 */
export const resetToUTC = (original) => {
  const date = new Date(original);
  date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
  return date;
};

const dateOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric'
};

/**
 * localiseDate formats the given Date object using the provided locale
 *
 * @param {string} locale user's locale in the lang-region format, e.g. `en-gb` or `sk-sk`
 * @param {Date} date date object to format
 * @returns {string|null} null if the Date or locale is invalid, string otherwise
 */
export const localiseDate = (locale, date) => {
  if (isNaN(date)) {
    return null;
  }
  try {
    return date.toLocaleDateString(locale, dateOptions);
  } catch (err) {
    return null;
  }
};

const relativeFormatterMap = new Map();

const defaultRelativeFormatterOptions = {
  numeric: 'auto',
  style: 'long',
};

// retrieves a memoized formatter to reduce memory usage
const getRelativeFormatterForLocale = (locale, options = {}) => {
  if (relativeFormatterMap.has(locale)) {
    return relativeFormatterMap.get(locale);
  }

  const formatter = new Intl.RelativeTimeFormat(
    locale,
    { ...defaultRelativeFormatterOptions, ...options },
  );
  relativeFormatterMap.set(locale, formatter);
  return formatter;
};

const relativeDateDivisions = [
  { amount: 60, name: 'seconds' },
  { amount: 60, name: 'minutes' },
  { amount: 24, name: 'hours' },
  { amount: 7, name: 'days' },
  { amount: 4.34524, name: 'weeks' },
  { amount: 12, name: 'months' },
  { amount: Number.POSITIVE_INFINITY, name: 'years' },
];

const millisecondsToSeconds = (milliseconds) => milliseconds / 1000;

/**
 * localiseTimeFromNow calculates the time between the provided Date and current time,
 * and returns a localised relative time string.
 *
 * @param {string} locale user's locale in the lang-region format, e.g. `en-gb` or `sk-sk`
 * @param {*} date date in the past, which will be compared to current date
 * @returns {string|null} null if the Date or locale is invalid, string otherwise
 */
export const localiseTimeFromNow = (locale, date) => {
  if (!Intl?.RelativeTimeFormat) {
    return null;
  }

  try {
    const formatter = getRelativeFormatterForLocale(locale);
    const now = new Date();
    let duration = millisecondsToSeconds(date - now);

    for (const division of relativeDateDivisions) {
      if (Math.abs(duration) < division.amount) {
        return formatter.format(Math.round(duration), division.name);
      }
      duration /= division.amount;
    }
  } catch (err) {
    return null;
  }

  return null;
};
